'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';


const workSteps = [
  {
    icon: 'ClipboardDocumentListIcon' as const,
    title: 'Audyt i analiza',
    body: 'Badamy Twoje obecne procesy, integracje i bolączki. Dokumentujemy wszystkie przepływy danych i identyfikujemy punkty krytyczne.',
    tag: 'Tydzień 1',
  },
  {
    icon: 'PencilSquareIcon' as const,
    title: 'Projekt wdrożenia',
    body: 'Tworzymy szczegółowy plan konfiguracji: reguły automatyzacji, mapowania pól, kolejność wdrożenia kanałów i harmonogram testów.',
    tag: 'Tydzień 1–2',
  },
  {
    icon: 'CogIcon' as const,
    title: 'Konfiguracja i integracje',
    body: 'Konfigurujemy system, podłączamy wszystkie kanały sprzedaży, ustawiamy reguły automatyzacji i testujemy każdą integrację oddzielnie.',
    tag: 'Tydzień 2–4',
  },
  {
    icon: 'BeakerIcon' as const,
    title: 'Testy z prawdziwymi danymi',
    body: 'Przeprowadzamy testy end-to-end na rzeczywistych zamówieniach. Weryfikujemy stany magazynowe, faktury i etykiety kurierskie.',
    tag: 'Tydzień 4–5',
  },
  {
    icon: 'ShieldCheckIcon' as const,
    title: 'Uruchomienie i stabilizacja',
    body: '30 dni wsparcia po uruchomieniu. Monitorujemy automatyzacje, reagujemy na błędy i optymalizujemy konfigurację pod rzeczywisty ruch.',
    tag: 'Tydzień 5+',
  },
];

interface JakPracujemyContent {
  title: string;
}

export default function JakPracujemySection() {
  const [content, setContent] = useState<JakPracujemyContent>({
    title: 'Od audytu do stabilizacji.',
  });
  const supabase = createClient();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['jak_pracujemy_title']);

        if (error) {
          console.error('Error fetching jak_pracujemy content:', error.message);
          return;
        }

        const settingsMap: Record<string, string> = {};
        data?.forEach((setting) => {
          settingsMap[setting.key] = setting.value;
        });

        setContent({
          title: settingsMap['jak_pracujemy_title'] || content.title,
        });
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchContent();
  }, [supabase]);

  return (
    <section id="jak-pracujemy" className="py-20 md:py-28 bg-offwhite">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14 space-y-3">
          <p className="section-label">Metodologia</p>
          <h2 className="text-4xl md:text-5xl font-900 tracking-tight text-navy leading-[0.95]">
            {content.title}
          </h2>
        </div>

        {/* Horizontal scrollable steps on mobile, grid on desktop */}
        <div className="grid md:grid-cols-5 gap-5">
          {workSteps.map((step, i) => (
            <div key={step.title} className="relative">
              {/* Connector line */}
              {i < workSteps.length - 1 && (
                <div
                  className="hidden md:block absolute top-8 left-[calc(100%+0.5rem)] w-5 h-0.5 bg-blue-DEFAULT/30 z-10"
                  aria-hidden="true"
                />
              )}

              <div className="bg-white rounded-2xl border border-border p-7 space-y-5 h-full">
                {/* Icon + number */}
                <div className="flex items-start justify-between">
                  <div className="p-2.5 rounded-xl bg-blue-pale">
                    <Icon name={step.icon} size={20} className="text-blue-DEFAULT" variant="outline" />
                  </div>
                  <span className="text-3xl font-900 text-navy/[0.07] tracking-tight leading-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>

                {/* Tag */}
                <span className="inline-block text-xs font-700 uppercase tracking-widest text-blue-DEFAULT">
                  {step.tag}
                </span>

                {/* Content */}
                <h3 className="text-base font-800 text-navy tracking-tight leading-snug">
                  {step.title}
                </h3>
                <p className="text-xs text-muted font-500 leading-relaxed">
                  {step.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}